import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';

interface DuplicateContact {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  leadScore: number;
  status: string;
  createdAt: string;
  events: number;
}

interface DuplicateGroup {
  reason: 'phone' | 'name';
  label: string;
  contacts: DuplicateContact[];
}

const REASON_LABELS: Record<string, string> = {
  phone: 'gleiche Telefonnummer',
  name: 'gleicher Name',
};

/**
 * Dublettenvorschläge.
 *
 * Bewusst ein Vorschlag und keine automatische Zusammenführung: die Kriterien
 * sind Indizien, keine Beweise. Wer entscheidet, sieht vorher, was er
 * zusammenlegt — inklusive Anzahl der Ereignisse, damit klar ist, welcher
 * Eintrag der gepflegtere ist.
 */
export function DuplicatesPage() {
  const queryClient = useQueryClient();
  const duplicates = useQuery({
    queryKey: ['contacts', 'duplicates'],
    queryFn: ({ signal }) =>
      api.get<{ groups: DuplicateGroup[] }>('/api/contacts/duplicates', signal),
  });

  const merge = useMutation({
    mutationFn: (input: { targetId: string; mergeIds: string[] }) =>
      api.post<{ moved: Record<string, number> }>(`/api/contacts/${input.targetId}/merge`, {
        mergeIds: input.mergeIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
    },
  });

  const groups = duplicates.data?.groups ?? [];

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/kontakte">Kontakte</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">Dubletten</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>Mögliche Dubletten</h1>
          <p className="muted text-sm">
            Beim Zusammenführen geht nichts verloren: Ereignisse, Deals, Aufgaben, Notizen und
            Punktebeiträge wandern auf den behaltenen Kontakt. Leere Felder werden aufgefüllt,
            gefüllte bleiben unberührt.
          </p>
        </div>
      </header>

      {merge.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {merge.error instanceof Error ? merge.error.message : 'Zusammenführen fehlgeschlagen.'}
        </div>
      )}

      {merge.isSuccess && (
        <div className="alert" data-tone="success" role="status">
          Zusammengeführt:{' '}
          {Object.entries(merge.data.moved)
            .filter(([, n]) => n > 0)
            .map(([table, n]) => `${n}× ${table}`)
            .join(', ') || 'keine verknüpften Daten'}
          .
        </div>
      )}

      {duplicates.isLoading && <p className="muted">Wird geprüft …</p>}

      {!duplicates.isLoading && groups.length === 0 && (
        <div className="card">
          <div className="card__body">
            <p className="muted">
              Keine Dubletten gefunden. Geprüft wird auf gleiche Telefonnummer (unabhängig von der
              Schreibweise) und auf identischen Vor- und Nachnamen.
            </p>
          </div>
        </div>
      )}

      {groups.map((group, index) => (
        <DuplicateCard
          key={`${group.reason}-${index}`}
          group={group}
          busy={merge.isPending}
          onMerge={(targetId, mergeIds) => merge.mutate({ targetId, mergeIds })}
        />
      ))}
    </div>
  );
}

function DuplicateCard({
  group,
  busy,
  onMerge,
}: {
  group: DuplicateGroup;
  busy: boolean;
  onMerge: (targetId: string, mergeIds: string[]) => void;
}) {
  // Voreinstellung: der Eintrag mit den meisten Ereignissen bleibt. Er hat die
  // längere Historie, und die des anderen wandert ohnehin mit.
  const suggested = [...group.contacts].sort((a, b) => b.events - a.events)[0]?.id ?? '';
  const [keep, setKeep] = useState(suggested);

  const others = group.contacts.filter((c) => c.id !== keep).map((c) => c.id);

  return (
    <section className="card">
      <div className="card__body stack">
        <h2 style={{ fontSize: 'var(--text-lg)' }}>
          {group.label} <span className="badge">{REASON_LABELS[group.reason] ?? group.reason}</span>
        </h2>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Behalten</th>
                <th scope="col">E-Mail</th>
                <th scope="col">Name</th>
                <th scope="col">Telefon</th>
                <th scope="col">Ereignisse</th>
                <th scope="col">Score</th>
                <th scope="col">Angelegt</th>
              </tr>
            </thead>
            <tbody>
              {group.contacts.map((contact) => (
                <tr key={contact.id}>
                  <td>
                    <input
                      type="radio"
                      name={`keep-${group.label}`}
                      checked={keep === contact.id}
                      onChange={() => setKeep(contact.id)}
                      aria-label={`${contact.email} behalten`}
                    />
                  </td>
                  <td>
                    <Link to="/kontakte/$id" params={{ id: contact.id }}>
                      {contact.email}
                    </Link>
                  </td>
                  <td>{[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}</td>
                  <td>{contact.phone ?? '—'}</td>
                  <td data-numeric>{contact.events}</td>
                  <td data-numeric>{contact.leadScore}</td>
                  <td>{new Date(contact.createdAt).toLocaleDateString('de-CH')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="dialog__actions">
          <Button
            onClick={() => onMerge(keep, others)}
            disabled={others.length === 0}
            loading={busy}
          >
            {others.length === 1 ? 'Zusammenführen' : `${others.length} Einträge zusammenführen`}
          </Button>
        </div>
      </div>
    </section>
  );
}
